/**
 * Glisser-déposer d'une liste verticale, au doigt comme au clavier.
 *
 * # Pourquoi ce code plutôt qu'une bibliothèque
 *
 * Les bibliothèques de drag-and-drop pèsent 30 à 50 ko, s'installent en
 * dépendance runtime et supposent un modèle de liste homogène. Ici il n'y a
 * qu'une liste, à hauteurs de ligne variables, sur un téléphone. Le besoin
 * réel tient en deux calculs (cf. `src/lib/reorder.ts`) et une boucle
 * d'animation.
 *
 * # Les trois règles qui évitent le comportement erratique
 *
 * 1. **On mesure une fois, au début du geste.** Remesurer pendant le
 *    déplacement, c'est mesurer une géométrie qu'on est en train de modifier :
 *    c'est ce qui produit les lignes qui se chevauchent et les trous.
 * 2. **Rien ne sort du flux.** La ligne déplacée reste à sa place dans le
 *    document et n'est que `transform`ée. Aucun clone, aucun `position:
 *    absolute`, donc aucune hauteur à reconstituer et aucun effondrement de la
 *    liste sous l'élément déplacé.
 * 3. **Les styles ne sont retirés qu'une fois les données réordonnées.** Les
 *    nettoyer au `pointerup` ferait réapparaître l'ancien ordre le temps que
 *    l'opération traverse la file et IndexedDB — un aller-retour visible.
 *
 * Le geste ne produit **qu'une seule opération**, émise au relâchement : rien
 * n'est écrit pendant le déplacement.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent, PointerEvent as ReactPointerEvent } from 'react';
import {
  clamp,
  clampToGroup,
  deltaBounds,
  shiftFor,
  targetIndex,
  type RowMetric,
} from '../lib/reorder.ts';

/** Bande, en bas et en haut du conteneur, qui déclenche le défilement auto. */
const EDGE = 72;
/** Vitesse maximale du défilement automatique, en pixels par frame. */
const MAX_SPEED = 16;
/** Filet de sécurité si l'ordre des données ne change jamais après un dépôt. */
const SETTLE_TIMEOUT = 400;

interface Options {
  /** Identifiants dans l'ordre exact du rendu. */
  ids: readonly string[];
  /**
   * Groupe d'appartenance. Un élément ne peut être déposé que dans le sien —
   * ici, les articles cochés et les autres (cf. `clampToGroup`).
   */
  groupOf: (id: string) => string;
  /** Appelé une seule fois, au dépôt, avec le rang visé dans `ids`. */
  onMove: (id: string, target: number) => void;
}

export interface RowHandle {
  ref: (element: HTMLLIElement | null) => void;
  dragging: boolean;
  handleProps: {
    onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
    onKeyDown: (event: ReactKeyboardEvent<HTMLElement>) => void;
  };
}

interface Drag {
  id: string;
  from: number;
  /** Ordre affiché au début du geste : s'il change, le relevé est périmé. */
  order: string;
  pointerId: number;
  scroller: HTMLElement;
  /** Bords supérieurs relevés au début du geste, dans un repère fixe. */
  metrics: RowMetric[];
  elements: (HTMLLIElement | undefined)[];
  height: number;
  groupFirst: number;
  groupLast: number;
  /** Amplitude autorisée : la ligne ne peut pas sortir de la bande du groupe. */
  bounds: { min: number; max: number };
  startClientY: number;
  startScrollTop: number;
  clientY: number;
  target: number;
  frame: number;
}

export function useReorder({ ids, groupOf, onMove }: Options): {
  listProps: { ref: (element: HTMLUListElement | null) => void; className: string };
  row: (id: string, index: number) => RowHandle;
} {
  const list = useRef<HTMLUListElement | null>(null);
  const rows = useRef(new Map<string, HTMLLIElement>());
  const drag = useRef<Drag | null>(null);
  const settling = useRef<string | null>(null);
  const settleTimer = useRef<number | null>(null);

  const [draggingId, setDraggingId] = useState<string | null>(null);

  const order = ids.join('|');

  // --- nettoyage des styles impératifs ------------------------------------

  const clearStyles = useCallback(() => {
    for (const element of rows.current.values()) {
      element.style.transform = '';
      element.style.willChange = '';
      element.style.zIndex = '';
      element.classList.remove('item--dragging');
    }
    settling.current = null;
    if (settleTimer.current !== null) {
      window.clearTimeout(settleTimer.current);
      settleTimer.current = null;
    }
  }, []);

  /**
   * Les transformations ne tombent qu'au rendu qui porte le nouvel ordre : la
   * ligne est alors déjà à sa place définitive, la transition est invisible.
   *
   * Le second passage est un filet de sécurité, et il compte : un `transform`
   * oublié sur quelques lignes creuse un **trou noir au milieu de la liste**
   * qui ne se referme jamais, y compris après un aller-retour dans une autre
   * liste. Au repos — aucun geste, aucun dépôt en cours de résorption — aucune
   * ligne n'a de raison de porter un style, quelle que soit la façon dont le
   * geste s'est terminé.
   */
  useLayoutEffect(() => {
    if (settling.current !== null && settling.current !== order) {
      clearStyles();
      return;
    }

    if (draggingId === null && settling.current === null) clearStyles();
  });

  useEffect(() => clearStyles, [clearStyles]);

  // --- boucle d'animation --------------------------------------------------

  const paint = useCallback(() => {
    const state = drag.current;
    if (!state) return;

    autoScroll(state);

    const delta = clamp(
      state.clientY - state.startClientY + (state.scroller.scrollTop - state.startScrollTop),
      state.bounds.min,
      state.bounds.max,
    );

    state.target = clampToGroup(
      targetIndex(state.metrics, state.from, delta),
      state.groupFirst,
      state.groupLast,
    );

    state.elements.forEach((element, index) => {
      if (!element) return;
      const offset =
        index === state.from
          ? delta
          : shiftFor(index, state.from, state.target, state.height);
      element.style.transform = offset === 0 ? '' : `translateY(${offset}px)`;
    });

    state.frame = requestAnimationFrame(paint);
  }, []);

  // --- geste ---------------------------------------------------------------

  const stop = useCallback(() => {
    const state = drag.current;
    if (!state) return null;

    cancelAnimationFrame(state.frame);
    drag.current = null;
    setDraggingId(null);
    return state;
  }, []);

  const finish = useCallback(
    (commit: boolean) => {
      const state = stop();
      if (!state) return;

      state.elements[state.from]?.classList.remove('item--dragging');

      if (!commit || state.target === state.from) {
        clearStyles();
        return;
      }

      // La ligne reste où le doigt l'a laissée : on ne retire les styles qu'au
      // rendu suivant, celui qui porte le nouvel ordre.
      settling.current = order;
      settleTimer.current = window.setTimeout(clearStyles, SETTLE_TIMEOUT);
      onMove(state.id, state.target);
    },
    [stop, clearStyles, onMove, order],
  );

  /**
   * Un delta peut arriver **pendant** le geste : l'autre appareil ajoute,
   * coche ou supprime un article, et la liste se réordonne sous le doigt. Les
   * hauteurs relevées au `pointerdown` ne décrivent alors plus rien, et
   * poursuivre le déplacement déposerait l'article n'importe où.
   *
   * On abandonne donc le geste sans rien écrire. Rare (il faut que les deux
   * appareils agissent dans la même seconde), et sans conséquence : il suffit
   * de recommencer.
   */
  useEffect(() => {
    if (draggingId !== null && drag.current && drag.current.order !== order) finish(false);
  }, [order, draggingId, finish]);

  useEffect(() => {
    if (draggingId === null) return;

    const move = (event: PointerEvent) => {
      const state = drag.current;
      if (!state || event.pointerId !== state.pointerId) return;
      // Sans ceci, iOS interprète le geste comme un défilement de page.
      event.preventDefault();
      state.clientY = event.clientY;
    };

    const up = (event: PointerEvent) => {
      if (drag.current && event.pointerId !== drag.current.pointerId) return;
      finish(true);
    };

    const cancel = () => finish(false);

    window.addEventListener('pointermove', move, { passive: false });
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', cancel);

    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', cancel);
    };
  }, [draggingId, finish]);

  const start = useCallback(
    (id: string, index: number, event: ReactPointerEvent<HTMLElement>) => {
      if (drag.current) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      const container = list.current;
      const moved = rows.current.get(id);
      if (!container || !moved) return;

      // Un dépôt encore en cours de résorption fausserait les mesures.
      clearStyles();

      const elements = ids.map((rowId) => rows.current.get(rowId));
      const metrics: RowMetric[] = elements.map((element) => {
        const rect = element?.getBoundingClientRect();
        return { top: rect?.top ?? 0, height: rect?.height ?? 0 };
      });

      const group = groupOf(id);
      let groupFirst = index;
      let groupLast = index;
      while (groupFirst > 0 && groupOf(ids[groupFirst - 1] as string) === group) groupFirst--;
      while (groupLast < ids.length - 1 && groupOf(ids[groupLast + 1] as string) === group) groupLast++;

      const scroller = scrollerOf(container);

      event.preventDefault();
      // La capture garantit qu'on reçoit le relâchement même si le doigt sort
      // du bouton — sans elle, une ligne peut rester « collée » au doigt.
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch {
        /* certains navigateurs refusent la capture : les écouteurs window suffisent */
      }

      moved.classList.add('item--dragging');
      moved.style.zIndex = '3';
      // `will-change` UNIQUEMENT sur la ligne saisie. L'appliquer à toute la
      // liste promeut chaque ligne en couche de composition : sur Android, une
      // liste un peu longue produit alors des couches que le navigateur ne
      // peint pas toujours — des rectangles noirs. La seule ligne qui bouge en
      // continu est celle qu'on déplace.
      moved.style.willChange = 'transform';

      drag.current = {
        id,
        from: index,
        order: ids.join('|'),
        pointerId: event.pointerId,
        scroller,
        metrics,
        elements,
        height: metrics[index]?.height ?? 0,
        groupFirst,
        groupLast,
        bounds: deltaBounds(metrics, index, groupFirst, groupLast),
        startClientY: event.clientY,
        startScrollTop: scroller.scrollTop,
        clientY: event.clientY,
        target: index,
        frame: requestAnimationFrame(paint),
      };

      setDraggingId(id);
    },
    [ids, groupOf, clearStyles, paint],
  );

  /** Réordonnancement au clavier : même opération, sans geste. */
  const nudge = useCallback(
    (id: string, index: number, direction: -1 | 1) => {
      const group = groupOf(id);
      const next = index + direction;
      if (next < 0 || next >= ids.length) return;
      if (groupOf(ids[next] as string) !== group) return;
      onMove(id, next);
    },
    [ids, groupOf, onMove],
  );

  const row = useCallback(
    (id: string, index: number): RowHandle => ({
      ref: (element) => {
        if (element) rows.current.set(id, element);
        else rows.current.delete(id);
      },
      dragging: draggingId === id,
      handleProps: {
        onPointerDown: (event) => start(id, index, event),
        onKeyDown: (event) => {
          if (event.key === 'ArrowUp') {
            event.preventDefault();
            nudge(id, index, -1);
          } else if (event.key === 'ArrowDown') {
            event.preventDefault();
            nudge(id, index, 1);
          }
        },
      },
    }),
    [draggingId, start, nudge],
  );

  return {
    listProps: {
      ref: (element) => {
        list.current = element;
      },
      className: draggingId === null ? 'items' : 'items items--dragging',
    },
    row,
  };
}

// ---------------------------------------------------------------------------

/** Premier ancêtre réellement défilant. */
function scrollerOf(element: HTMLElement): HTMLElement {
  let node: HTMLElement | null = element.parentElement;

  while (node) {
    // On ne teste PAS `scrollHeight > clientHeight` : une liste qui tient
    // juste à l'écran au début du geste reste le conteneur défilant, et c'est
    // lui qu'il faut viser dès qu'elle déborde.
    const overflow = getComputedStyle(node).overflowY;
    if (overflow === 'auto' || overflow === 'scroll') return node;
    node = node.parentElement;
  }

  return (document.scrollingElement as HTMLElement | null) ?? document.documentElement;
}

/**
 * Défilement automatique quand le doigt atteint un bord.
 *
 * Indispensable sur téléphone : sans lui, on ne peut pas déplacer un article
 * au-delà de ce qui tient à l'écran, ce qui rend la fonction inutilisable dès
 * qu'une liste dépasse une dizaine d'articles.
 */
function autoScroll(state: Drag): void {
  const { scroller, clientY } = state;
  const document_ = document.scrollingElement;
  const top = scroller === document_ ? 0 : scroller.getBoundingClientRect().top;
  const bottom = scroller === document_ ? window.innerHeight : top + scroller.clientHeight;

  let speed = 0;
  if (clientY < top + EDGE) speed = -ratio(top + EDGE - clientY) * MAX_SPEED;
  else if (clientY > bottom - EDGE) speed = ratio(clientY - (bottom - EDGE)) * MAX_SPEED;
  if (speed === 0) return;

  const max = scroller.scrollHeight - scroller.clientHeight;
  scroller.scrollTop = Math.max(0, Math.min(scroller.scrollTop + speed, max));
}

/** Accélération progressive : lente au bord de la zone, maximale au ras. */
function ratio(distance: number): number {
  return Math.min(1, distance / EDGE);
}
